// src/Register.js
import React, { useState, useCallback } from 'react';
import { saveUserSession } from './utils/cookies';
import { getTranslation } from './languages';
import { API_ENDPOINTS } from './config';
import { fetchWithRetry, RateLimitError } from './utils/rateLimitHandler';

export default function Register({ onRegisterSuccess, onSwitchToLogin, uiLanguage = 'en' }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState('');

    const t = useCallback((key) => getTranslation(key, uiLanguage), [uiLanguage]);

    // Email validation function
    const validateEmail = useCallback((emailValue) => {
        if (!emailValue) {
            setEmailError('');
            return true;
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) {
            setEmailError(t('invalidEmail') || 'Please enter a valid email address');
            return false;
        }
        
        setEmailError('');
        return true;
    }, [t]);

    // Handle email blur event
    const handleEmailBlur = useCallback(() => {
        validateEmail(email);
    }, [email, validateEmail]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        // Client-side validation
        if (!email || !password || !confirmPassword) {
            setError(t('allFieldsRequired') || 'All fields are required');
            return;
        }
        
        // Validate email format
        if (!validateEmail(email)) {
            return;
        }
        
        if (password !== confirmPassword) {
            setError(t('passwordsDoNotMatch') || 'Passwords do not match');
            return;
        }
        
        if (password.length < 6) {
            setError(t('passwordTooShort') || 'Password must be at least 6 characters long');
            return;
        }
        
        setIsLoading(true);
        
        try {
            const response = await fetchWithRetry(
                async () => fetch(API_ENDPOINTS.REGISTER, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, password, confirmPassword, language: uiLanguage }),
                }),
                {
                    onRetry: ({ attempt, delay, maxRetries }) => {
                        setError(getTranslation('retryingRequest', uiLanguage, { seconds: Math.round(delay/1000) }));
                    }
                }
            );

            const data = await response.json();

            if (response.ok && data.success) {
                if (data.requiresVerification) {
                    // Show success message for email verification
                    setRegistrationSuccess(true);
                    setRegisteredEmail(data.email || email);
                    setError('');
                    // If there's a custom message (e.g., for existing unverified accounts), show it
                    if (data.message && data.message.includes('already exists')) {
                        // Account already exists but unverified - still show success
                    }
                } else {
                    // Old flow for backwards compatibility (shouldn't happen with new registrations)
                    
                    // Save user session to cookies
                    saveUserSession(data.userId, data.username, data.sessionId);
                    
                    // Store session token
                    if (data.sessionId) {
                        localStorage.setItem('sessionId', data.sessionId);
                        localStorage.setItem('sessionExpiresAt', data.expiresAt);
                    }
                    
                    // Call success callback
                    onRegisterSuccess(data.userId, { email: data.username });
                }
            } else {
                // Check for whitelist error
                if (data.errorCode === 'NOT_WHITELISTED') {
                    setError(t('notWhitelisted') || data.error || 'Sorry, only invited test users can create an account at this time.');
                } else {
                    setError(data.error || t('registrationFailed') || 'Registration failed');
                }
                
                // Handle specific error cases
                if (response.status === 409) {
                    setError(t('emailAlreadyRegistered') || 'This email is already registered');
                }
            }
        } catch (err) {
            console.error('[Register] Registration error:', err);
            if (err instanceof RateLimitError) {
                setError(t('rateLimitExceeded') || 'Too many attempts. Please try again later.');
            } else {
                setError(t('connectionError') || 'Connection error. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Show success message after registration
    if (registrationSuccess) {
        return (
            <div className="max-w-md w-full mx-auto">
                <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden">
                    <div className="px-8 pt-8 pb-6">
                        <div className="text-center mb-8">
                            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
                                {t('verificationEmailSent') || 'Check Your Email!'}
                            </h1>
                            <p className="text-gray-600 mb-4">
                                {t('verificationEmailDescription') || `We've sent a verification email to:`}
                            </p>
                            <p className="text-lg font-semibold text-blue-600 mb-6">{registeredEmail}</p>
                            
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                                <h3 className="font-semibold text-blue-900 mb-2">
                                    {t('nextSteps') || 'Next Steps:'}
                                </h3>
                                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                                    <li>{t('checkInbox') || 'Check your email inbox'}</li>
                                    <li>{t('clickVerifyLink') || 'Click the verification link in the email'}</li>
                                    <li>{t('loginAfterVerification') || 'Log in with your credentials'}</li>
                                </ol>
                            </div>
                            
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                                <p className="text-sm text-yellow-800">
                                    <strong>{t('cantFindEmail') || "Can't find the email?"}</strong><br />
                                    {t('checkSpamFolder') || 'Check your spam folder or request a new verification email from the login page.'}
                                </p>
                            </div>
                            
                            <button
                                onClick={onSwitchToLogin}
                                className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-lg shadow-md transform transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                {t('goToLogin') || 'Go to Login'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="max-w-md w-full mx-auto">
            <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden">
                <div className="px-8 pt-8 pb-6">
                    <div className="text-center mb-8">
                        <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                        </div>
                        <h1 className="text-3xl font-extrabold text-gray-900 mb-2">{t('createAccount') || 'Create Account'}</h1>
                        <p className="text-gray-600">{t('registerDescription') || 'Sign up to start your learning journey'}</p>
                    </div>
            
                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center animate-shake">
                            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Registration Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-1">
                            <label className="block text-sm font-semibold text-gray-700">{t('email') || 'Email'}</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        // Clear email error when user starts typing
                                        if (emailError) setEmailError('');
                                    }}
                                    onBlur={handleEmailBlur}
                                    className={`w-full pl-10 pr-3 py-3 border ${emailError ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:border-gray-400`}
                                    placeholder={t('enterEmail') || 'Enter your email'}
                                    autoComplete="email"
                                    disabled={isLoading}
                                />
                            </div>
                            {emailError && (
                                <p className="text-xs text-red-600 mt-1">{emailError}</p>
                            )}
                        </div>
                        
                        <div className="space-y-1">
                            <label className="block text-sm font-semibold text-gray-700">{t('password') || 'Password'}</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                                    placeholder={t('enterPassword') || 'Enter password'}
                                    autoComplete="new-password"
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {showPassword ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{t('passwordRequirement') || 'At least 6 characters'}</p>
                        </div>
                        
                        <div className="space-y-1">
                            <label className="block text-sm font-semibold text-gray-700">{t('confirmPassword') || 'Confirm Password'}</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                                    placeholder={t('confirmPasswordPlaceholder') || 'Confirm your password'}
                                    autoComplete="new-password"
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {showConfirmPassword ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        )}
                                    </svg>
                                </button>
                            </div>
                        </div>
                        
                        <button 
                            type="submit" 
                            disabled={isLoading}
                            className="w-full py-3 px-4 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white font-semibold rounded-lg shadow-md transform transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t('creatingAccount') || 'Creating Account...'}
                                </span>
                            ) : (
                                t('createAccount') || 'Create Account'
                            )}
                        </button>
                    </form>

                    {/* Switch to Login */}
                    <div className="mt-6 text-center">
                        <p className="text-sm text-gray-600">
                            {t('alreadyHaveAccount') || 'Already have an account?'}{' '}
                            <button
                                onClick={onSwitchToLogin}
                                className="font-medium text-green-600 hover:text-green-500 focus:outline-none focus:underline transition duration-150"
                            >
                                {t('signIn') || 'Sign in'}
                            </button>
                        </p>
                    </div>
                </div>
                
                {/* Footer */}
                <div className="px-8 py-4 bg-gray-50 border-t border-gray-100">
                    <p className="text-xs text-center text-gray-500">
                        {t('secureConnection') || 'Your information is encrypted and secure'}
                    </p>
                </div>
            </div>
            
            <style>{`
                @keyframes shake {
                    0%, 100% {
                        transform: translateX(0);
                    }
                    10%, 30%, 50%, 70%, 90% {
                        transform: translateX(-2px);
                    }
                    20%, 40%, 60%, 80% {
                        transform: translateX(2px);
                    }
                }
                
                .animate-shake {
                    animation: shake 0.5s ease-in-out;
                }
            `}</style>
        </div>
    );
}